/**
 * Профиль пользователя: второй канал связи и видимые сессии.
 *
 * Экрана профиля в каркасе кабинета не было: он пришёл вместе с двумя
 * требованиями, адресованными этому блоку запечатанной спецификацией
 * предыдущего.
 *
 * Проверок формата канала здесь нет ни одной. Форма тела — та же, что у входа:
 * ключ канала ровно один, и какой именно, выбирает клиент; годность значения
 * проверяет сервер. Своя проверка разошлась бы с серверной молча.
 *
 * Перерисовка после подтверждения идёт **по ответу**, а не по введённому:
 * подтверждение возвращает обновлённый профиль, и показывать набранное значило
 * бы показывать то, чего сервер, возможно, не принял.
 *
 * Признака «текущая сессия», вычисленного клиентом, нет ни одного: он приходит
 * полем ответа. «Первая в списке» и «самая свежая» — догадки, которых сервер не
 * обещал, и на первом же ответе с другим порядком они расходятся с истиной.
 *
 * Завершение текущей сессии уводит на вход: токен отзывается немедленно, и
 * вкладка, оставшаяся с отвергнутым токеном, повторяет отказ на каждом
 * действии, выглядя работающей.
 *
 * Дата рождения и пол — блок `WEB-2.4` (`FR-WEB-105`, решение формы `Б19`):
 * значения из профиля, правка — запечатанной операцией правки профиля с
 * языком **текущего профиля** (язык этим не меняется). Пустая дата и «не
 * указан» уходят `null` — так контракт называет «стереть». Границ даты клиент
 * не знает: «не позже сегодня» и «не раньше» судит сервер, его отказ ложится
 * под полем, а введённое остаётся. После ответа поля встают **по ответу**, а
 * не по введённому. Подпись «указано самим человеком» стоит рядом: членам
 * фирмы сервер отдаёт возраст и пол, дату — только самому человеку.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient, AuthCodeInput } from '../api/client';
import type { CurrentUserData, Session, UserUpdateRequest } from '../api/contract';
import { GENDERS } from '../api/generated/enums';
import { activeLocale, genderStringKey } from '../i18n';
import type { Strings } from '../i18n';
import {
  createFieldMessageSlot,
  enumLabel,
  refusalText,
  renderFieldMessages,
  renderScreenState,
  screenStateFor,
  selectEnumOption,
} from './state';
import type { ScreenState } from './state';

export interface ProfileScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  /** Токен отозван сервером: вкладке остаётся вернуться на вход. */
  readonly onSignedOut: () => void;
}

export interface ProfileScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — содержимое показано. */
  readonly state: ScreenState | null;
  /** Сессии в том порядке, в котором их вернул сервер. */
  readonly sessions: readonly Session[];
  load(): Promise<void>;
  /** Первая стадия второго канала: тело уходит одним ключом канала. */
  requestChannelCode(input: AuthCodeInput): Promise<void>;
  /** Вторая стадия: тот же канал плюс код. */
  confirmChannel(code: string): Promise<void>;
  terminateSession(sessionId: string): Promise<void>;
  /** Дата рождения и пол как введены: пустое — `null`, то есть «стереть». */
  savePersonal(input: PersonalInput): Promise<void>;
  /**
   * Переподписать экран на новом языке, не трогая введённого (долг `TD-72`,
   * ответ на ревью Р8): несохранённые дата рождения и пол остаются в полях.
   */
  relabel(next: Strings): void;
}

/**
 * Введённое человеком в форму даты рождения и пола — строками полей. Пола нет
 * — пол не уходит вовсе: «не прислано — не трогает» (`UserUpdateRequest`).
 * Так уходит значение вне перечня, которого человек не трогал (ответ на ревью
 * Р5): клиент не знает его подписи, но и стирать его не вправе.
 */
export interface PersonalInput {
  readonly birthDate: string;
  readonly gender?: string;
}

/** Имя поля кода — то же, что имя поля запроса: по нему сервер называет место. */
const CODE_FIELD = 'code';

export function createProfileScreen(options: ProfileScreenOptions): ProfileScreen {
  const { client, onSignedOut } = options;
  let { strings } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const contacts = document.createElement('p');
  const form = document.createElement('form');
  const channelCaption = document.createElement('label');
  const channelCaptionText = document.createElement('span');
  const channelField = document.createElement('input');
  const channelMessage = createFieldMessageSlot();
  const codeCaption = document.createElement('label');
  const codeCaptionText = document.createElement('span');
  const codeField = document.createElement('input');
  const codeMessage = createFieldMessageSlot();
  const submit = document.createElement('button');
  const sessionsHeading = document.createElement('h2');
  const sessionsList = document.createElement('ul');
  const personalForm = document.createElement('form');
  const birthCaption = document.createElement('label');
  const birthCaptionText = document.createElement('span');
  const birthField = document.createElement('input');
  const birthMessage = createFieldMessageSlot();
  const genderCaption = document.createElement('label');
  const genderCaptionText = document.createElement('span');
  const genderField = document.createElement('select');
  const genderMessage = createFieldMessageSlot();
  const personalNote = document.createElement('p');
  const personalHint = document.createElement('p');
  const personalNotice = createFieldMessageSlot();
  const personalSubmit = document.createElement('button');

  element.className = 'screen screen-profile';
  form.className = 'form';
  channelCaption.className = 'form-field';
  channelCaptionText.className = 'form-field__caption';
  codeCaption.className = 'form-field';
  codeCaptionText.className = 'form-field__caption';
  submit.className = 'action';

  codeField.name = CODE_FIELD;
  submit.type = 'submit';

  channelCaption.append(channelCaptionText, channelField, channelMessage);
  codeCaption.append(codeCaptionText, codeField, codeMessage);

  personalForm.className = 'form';
  birthCaption.className = 'form-field';
  birthCaptionText.className = 'form-field__caption';
  genderCaption.className = 'form-field';
  genderCaptionText.className = 'form-field__caption';
  personalNote.className = 'form-hint';
  personalHint.className = 'form-hint';
  personalSubmit.className = 'action';
  personalSubmit.type = 'submit';

  // Имена полей — имена полей тела правки профиля: по ним сервер называет
  // место своего сообщения. Поле даты — календарное, без `min` и `max`:
  // границы судит сервер.
  birthField.type = 'date';
  birthField.name = 'birth_date';
  genderField.name = 'gender';

  // Выбор пола — порождённый перечень **целиком** плюс «не указан» (пустое
  // значение — `null` в теле, то есть «стереть»). Подпись — по строителю.
  const genderUnset = document.createElement('option');

  genderUnset.value = '';
  genderField.append(genderUnset);

  for (const gender of GENDERS) {
    const choice = document.createElement('option');

    choice.value = gender;
    genderField.append(choice);
  }

  // Значение пола вне перечня (CR-007; ответ на ревью Р5) — отдельный вариант
  // «неизвестно» без исходной строки. Он стоит в выборе, только пока такое
  // значение пришло, и выбранный значит «не трогать»: пол не уходит вовсе.
  // Без него браузер выбрал бы первый вариант — ложное «не указан», — и
  // сохранение одной даты стёрло бы пол молча.
  const genderUnknown = document.createElement('option');

  /**
   * Подписи экрана — одним местом: смена языка переподписывает их на месте
   * (ответ на ревью Р8), поля форм остаются теми же узлами с введённым.
   * Варианты пола — по строителю; «неизвестно» — подписью значения вне
   * перечня, без исходной строки.
   */
  function caption(): void {
    heading.textContent = strings.profile_title;
    channelCaptionText.textContent = strings.profile_channel_add;
    codeCaptionText.textContent = strings.profile_channel_confirm;
    sessionsHeading.textContent = strings.profile_sessions_title;
    birthCaptionText.textContent = strings.profile_birth_date;
    genderCaptionText.textContent = strings.profile_gender;
    personalNote.textContent = strings.profile_personal_note;
    personalHint.textContent = strings.profile_personal_hint;
    personalSubmit.textContent = strings.profile_personal_save;
    genderUnset.textContent = strings.profile_gender_unspecified;

    for (const option of genderField.querySelectorAll('option')) {
      if (option !== genderUnset) {
        option.textContent = enumLabel(strings, genderStringKey(option.value));
      }
    }
  }

  caption();

  birthCaption.append(birthCaptionText, birthField, birthMessage);
  genderCaption.append(genderCaptionText, genderField, genderMessage);
  personalForm.append(personalNotice, birthCaption, genderCaption, personalNote, personalHint, personalSubmit);

  let state: ScreenState | null = 'loading';
  let profile: CurrentUserData | null = null;
  let sessions: readonly Session[] = [];
  /** Канал, которым запрошен код, и значение, которым его запросили. */
  let asked: AuthCodeInput | null = null;

  /**
   * Канал, которого у человека нет, — тот, который и добавляется.
   *
   * Это не правило: какой канал главный и что делать со вторым, решает сервер.
   * Здесь выбирается лишь **имя поля тела**, и выбирается оно по тому, что
   * пришло в профиле.
   */
  function missingChannel(): 'phone' | 'email' | null {
    if (profile === null) {
      return null;
    }

    if (profile.email === null) {
      return 'email';
    }

    return profile.phone === null ? 'phone' : null;
  }

  function contactLine(): string {
    if (profile === null) {
      return '';
    }

    return `${profile.phone ?? ''} ${profile.email ?? ''}`.trim();
  }

  function sessionRow(session: Session): HTMLElement {
    const row = document.createElement('li');
    const line = document.createElement('span');
    const stop = document.createElement('button');

    row.className = 'session-row';
    // Устройство, оба времени и признак текущей — как их отдал сервер. Время
    // последнего использования, равное null, показывается подписью: пустое
    // место читается как «поля нет», и это ложь.
    line.textContent = [
      session.device_name,
      session.created_at,
      session.last_used_at ?? strings.profile_session_never_used,
      session.current ? strings.profile_session_current : '',
    ].join(' ');

    stop.type = 'button';
    stop.className = 'action';
    stop.textContent = strings.profile_session_terminate;
    stop.addEventListener('click', () => {
      void terminateSession(session.id);
    });

    row.append(line, stop);

    return row;
  }

  function renderSessions(): void {
    if (sessions.length === 0) {
      sessionsList.replaceChildren(renderScreenState('empty', strings));

      return;
    }

    const rows: HTMLElement[] = [];

    for (const session of sessions) {
      rows.push(sessionRow(session));
    }

    sessionsList.replaceChildren(...rows);
  }

  function render(): void {
    if (state !== null) {
      element.replaceChildren(renderScreenState(state, strings));

      return;
    }

    const channel = missingChannel();

    contacts.textContent = contactLine();

    if (channel === null) {
      // Добавлять нечего: оба канала заполнены. Форма без поля обещала бы
      // действие, которого нет.
      form.replaceChildren();
    } else {
      channelField.name = channel;
      channelField.id = channel;

      form.replaceChildren(
        channelCaption,
        ...(asked === null ? [] : [codeCaption]),
        submit,
      );
    }

    submit.textContent =
      asked === null ? strings.profile_channel_code_request : strings.profile_channel_confirm;

    renderSessions();
    element.replaceChildren(heading, contacts, personalForm, form, sessionsHeading, sessionsList);
  }

  /**
   * Поля даты рождения и пола — **по профилю из ответа**. Зовётся после
   * чтения профиля и после удавшегося сохранения, но не при перерисовке:
   * отказ сервера оставляет в полях введённое, и человеку есть что исправить.
   */
  function fillPersonal(): void {
    if (profile === null) {
      return;
    }

    birthField.value = profile.birth_date ?? '';

    const shownKey = genderStringKey(profile.gender ?? '');
    let listed = false;

    genderUnknown.remove();

    for (const option of genderField.querySelectorAll('option')) {
      if (genderStringKey(option.value) === shownKey) {
        listed = true;
      }
    }

    if (!listed) {
      genderUnknown.value = profile.gender ?? '';
      genderField.append(genderUnknown);
      caption();
    }

    selectEnumOption(genderField, shownKey, genderStringKey);
  }

  /** Выбран ли вариант «неизвестно» — значение вне перечня, которого не трогали. */
  function genderLeftUnknown(): boolean {
    return genderUnknown.parentElement === genderField && genderUnknown.selected;
  }

  /**
   * Исход неудачного обращения — одним местом на весь экран.
   *
   * Сервер, назвавший поля, получает свои сообщения у этих полей, и содержимое
   * экрана остаётся: человеку есть что исправлять. Сервер, не назвавший ни
   * одного поля, оставляет экрану только состояние отказа — показывать рядом с
   * ним содержимое значило бы делать вид, что ничего не случилось.
   */
  function failureOf(error: unknown): void {
    const failure = error instanceof ApiFailure ? error : null;

    if (failure !== null && failure.details.length > 0) {
      render();
      renderFieldMessages(form, failure.details);

      return;
    }

    state = screenStateFor({ kind: 'failed', code: failure === null ? null : failure.code });
    render();
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    render();

    try {
      profile = await client.getCurrentUser();
      sessions = await client.listSessions();
      state = null;
      fillPersonal();
    } catch (error) {
      profile = null;
      sessions = [];
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  async function requestChannelCode(input: AuthCodeInput): Promise<void> {
    renderFieldMessages(form, []);

    try {
      await client.requestChannelCode(input);

      asked = input;
      state = null;
      render();
    } catch (error) {
      asked = null;
      failureOf(error);
    }
  }

  async function confirmChannel(code: string): Promise<void> {
    if (asked === null) {
      return;
    }

    renderFieldMessages(form, []);

    const input = 'phone' in asked ? { phone: asked.phone, code } : { email: asked.email, code };

    try {
      // Источник истины — ответ: профиль перерисовывается пришедшим, а не
      // набранным. Введённое значение сервер вправе привести к своей форме.
      profile = await client.confirmChannel(input);
      asked = null;
      channelField.value = '';
      codeField.value = '';
      state = null;
      render();
    } catch (error) {
      failureOf(error);
    }
  }

  /**
   * Сохранение даты рождения и пола. Язык уходит **профиля**, а не выбранный
   * где-то ещё: операция правки профиля требует его, и этим действием он не
   * меняется. Отказ — сообщения сервера под полями, общий текст — над формой;
   * экран остаётся на содержимом.
   */
  async function savePersonal(input: PersonalInput): Promise<void> {
    if (profile === null) {
      return;
    }

    personalNotice.textContent = '';
    renderFieldMessages(personalForm, []);

    const birth = input.birthDate === '' ? null : input.birthDate;
    const body: UserUpdateRequest = { locale: profile.locale, birth_date: birth };

    if (input.gender !== undefined) {
      body.gender = input.gender === '' ? null : (input.gender as NonNullable<UserUpdateRequest['gender']>);
    }

    try {
      profile = await client.updateCurrentUser(body);
      fillPersonal();
      render();
    } catch (error) {
      const failure = error instanceof ApiFailure ? error : null;
      const homeless = renderFieldMessages(personalForm, failure?.details ?? []);
      const texts: string[] = [];

      for (const detail of homeless) {
        texts.push(detail.message);
      }

      if (texts.length === 0 && (failure === null || failure.details.length === 0)) {
        texts.push(refusalText(error, strings));
      }

      personalNotice.textContent = texts.join(' ');
    }
  }

  /** Та ли это сессия, которой выполнен текущий запрос, — по полю ответа. */
  function isCurrent(sessionId: string): boolean {
    for (const session of sessions) {
      if (session.id === sessionId) {
        return session.current;
      }
    }

    return false;
  }

  async function terminateSession(sessionId: string): Promise<void> {
    const wasCurrent = isCurrent(sessionId);

    try {
      await client.terminateSession(sessionId);
    } catch (error) {
      failureOf(error);

      return;
    }

    if (wasCurrent) {
      // Токен отозван немедленно — так записано в контракте. Перечитывать
      // список нечем: следующее обращение получило бы отказ.
      onSignedOut();

      return;
    }

    try {
      sessions = await client.listSessions();
    } catch (error) {
      failureOf(error);

      return;
    }

    render();
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    const channel = missingChannel();

    if (channel === null) {
      return;
    }

    if (asked !== null) {
      void confirmChannel(codeField.value);

      return;
    }

    const value = channelField.value;

    void requestChannelCode(channel === 'phone' ? { phone: value } : { email: value });
  });

  personalForm.addEventListener('submit', (event) => {
    event.preventDefault();

    void savePersonal(
      genderLeftUnknown() ? { birthDate: birthField.value } : { birthDate: birthField.value, gender: genderField.value },
    );
  });

  /**
   * Переподписать экран на новом языке (долг `TD-72`, ответ на ревью Р8): поля
   * даты рождения, пола и второго канала — те же узлы с введённым, меняются
   * только подписи; сессии перерисовываются из уже пришедшего ответа. Язык
   * профиля — тот, что оболочка применила **по ответу** смены языка: иначе
   * следующее «Сохранить» отправило бы в теле прежний язык и вернуло его
   * молча.
   */
  function relabel(next: Strings): void {
    strings = next;

    if (profile !== null) {
      profile = { ...profile, locale: activeLocale() };
    }

    caption();
    render();
  }

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    get sessions(): readonly Session[] {
      return sessions;
    },

    load,
    requestChannelCode,
    confirmChannel,
    terminateSession,
    savePersonal,
    relabel,
  };
}
